/**
 * Company Search — internal contacts (sealed element d, annotation 3).
 *
 * Consumes get_company_contacts for the resolved org. Each row renders display
 * name, title, temperature, owner, source group, and last interaction; every
 * absent field renders honest absence (the field is simply omitted, never
 * fabricated). The server returns the full set PRE-ORDERED (temperature, then
 * recency), so the client preserves that order and never re-sorts. The card
 * collapses to the first three (annotation 3's three-name state) with View All
 * revealing the full set; the header count is the returned set size.
 */

import { useState } from "react";
import { useCompanyContacts } from "../hooks";
import { collapseContacts } from "../logic";
import type { CompanyContactView } from "../types";
import { IconContacts } from "./icons";

function shortDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

/** Temperature emphasis stays monochrome: warmer = teal accent, colder = muted
 *  grey, both from sealed tokens — no thermal (red/amber) hue is introduced. */
function tempClass(temperature: string): string {
  switch (temperature.toLowerCase()) {
    case "hot":
      return "sxco-temp--hot";
    case "cold":
      return "sxco-temp--cold";
    default:
      return "";
  }
}

function titleCase(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

function ContactRow({ contact }: { contact: CompanyContactView }): JSX.Element {
  const last = shortDate(contact.lastInteraction);
  const hasMeta = contact.ownerName || contact.sourceGroup || last;
  return (
    <li className="sxco-contact">
      <div className="sxco-contact-top">
        <span className="sxco-contact-name">{contact.displayName}</span>
        {contact.temperature && (
          <span className={`sxco-chip sxco-chip--sm sxco-temp ${tempClass(contact.temperature)}`}>
            {titleCase(contact.temperature)}
          </span>
        )}
      </div>
      {contact.title && <p className="sxco-contact-title">{contact.title}</p>}
      {hasMeta && (
        <div className="sxco-contact-meta">
          {contact.ownerName && <span>Owner: {contact.ownerName}</span>}
          {contact.sourceGroup && <span>{contact.sourceGroup}</span>}
          {last && <span>{last}</span>}
        </div>
      )}
    </li>
  );
}

export function InternalContacts({ organizationId }: { organizationId: string }): JSX.Element {
  const { data, isPending, isError, error } = useCompanyContacts(organizationId);
  const [expanded, setExpanded] = useState(false);
  const contacts = data ?? [];
  const visible = collapseContacts(contacts, expanded);

  return (
    <section className="sxco-card">
      <p className="sxco-card-head">
        <span className="sxco-card-icon" aria-hidden="true">
          <IconContacts />
        </span>
        Internal contacts
        {contacts.length > 0 && <span className="sxco-count">{contacts.length}</span>}
      </p>

      {isPending && <p className="sxco-muted">Loading internal contacts…</p>}

      {/* Error only when nothing ever loaded: a failed background refetch (e.g.
          on remount to the same company) keeps the last-good roster — including
          a known-empty one — rather than masking it with an error. */}
      {isError && data === undefined && (
        <p className="sxco-error">
          {error instanceof Error ? error.message : "Failed to load internal contacts."}
        </p>
      )}

      {data !== undefined && contacts.length === 0 && (
        <p className="sxco-muted">No internal contacts are available from this view yet.</p>
      )}

      {contacts.length > 0 && (
        <>
          <ul className="sxco-contacts-list">
            {visible.map((c) => (
              <ContactRow key={c.id} contact={c} />
            ))}
          </ul>
          {contacts.length > 3 && (
            <button
              type="button"
              className="sxco-linkbtn sxco-contacts-toggle"
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded ? "Show less" : `View all (${contacts.length})`}
            </button>
          )}
        </>
      )}
    </section>
  );
}
